<template>
  <Dropdown ref='drop'
    stickRight
    :dropContentClass="dropContentClass"
    class="drop-down labels-drop flex items-center justify-center"
    @open="onDropdownOpen"
  >
    <template #wrapper>
      <slot name="activator"></slot>
    </template>
    <template #items>
      <AppSearchBox
        ref="tagsSearch"
        class="w-56"
        :closeOnSelect="false"
        :items="labels"
        :getLabel="label => label.name"
        :placeholder="t('task.search_or_create_tag')"
        @select="switchLabel"
        @enterQuery="createLabelIfNew"
      >
        <template #item="{ item }">
          <div
            class="tag-tint flex h-4.5 min-w-5 shrink items-center overflow-hidden rounded-lg
                   px-2 py-0.5 text-unit font-bold leading-none"
            :style="{ '--tag-color': `#${item.color}` }"
          >
            <div class="truncate" :title="item.name">{{ item.name }}</div>
          </div>
          <IconCheck
            v-if="isSelected(item)"
            class="ml-auto size-3.5 flex-none text-accent"
          />
        </template>
        <template #empty="{ query }">
          <div class="flex min-h-7.5 items-center gap-2 text-label text-muted">
            <template v-if="!query">{{ t('addTask.no_tags') }}</template>
            <template v-else-if="labelNameTaken(query)">{{ t('addTask.nothing_found') }}</template>
            <template v-else>
              <div
                class="tag-chip tag-tint"
                :style="{ '--tag-color': `#${labelColorFor(query)}` }"
                @click="createLabel(query)"
              >
                <span class="truncate">{{ query }}</span>
                <IconPlus class="size-3.5 flex-none" />
              </div>
              <span class="shrink-0">{{ t('addTask.create_tag_hint') }}</span>
            </template>
          </div>
        </template>
      </AppSearchBox>
    </template>
  </Dropdown>
</template>

<script setup>
import { IconCheck, IconPlus } from '@iconify-prerendered/vue-tabler';
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import AppSearchBox from '@/components/elements/AppSearchBox';
import { labelColorFor } from '@/utils/tagColors';

const { t } = useI18n();

// Props
const props = defineProps({
  task: Object,
  project: Object,
  dropContentClass: {
    type: String,
    default: '',
  },
});

// Emits
const emit = defineEmits(['set']);

// Refs
const drop = ref(null);
const tagsSearch = ref(null);
const instanceToken = Symbol('task-label-select');
// selection frozen on open, so rows don't jump while toggling
const activeLabelIds = ref([]);

// Computed properties
// tasks without tags come from the API without the labels field
const taskLabels = computed(() => props.task.labels || []);

const labels = computed(() => {
  const activeIds = new Set(activeLabelIds.value);
  return [...props.project.labels.filter(l => !l.archived)].sort((a, b) => {
    const aActive = activeIds.has(a.id);
    const bActive = activeIds.has(b.id);
    if (aActive !== bActive) return aActive ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
});

// Methods
const isSelected = label => taskLabels.value.some(tl => tl.id === label.id);

const switchLabel = (label) => {
  emit('set', {
    task: {
      id: props.task.id,
      stageId: props.task.stageId,
      projectId: props.task.projectId,
      labels: taskLabels.value,
    },
    label,
    ...(isSelected(label) ? { clear: true } : { add: true }),
  });
};

const labelNameTaken = name => props.project.labels
  .some(l => l.name.toLowerCase() === name.toLowerCase());

const createLabel = (name) => {
  const label = {
    id: window.utils.genId(),
    color: labelColorFor(name),
    name,
    tasksCount: 0,
  };
  emit('set', {
    task: {
      id: props.task.id,
      stageId: props.task.stageId,
      projectId: props.task.projectId,
      labels: taskLabels.value,
    },
    label,
    add: true,
  });
  activeLabelIds.value = [...activeLabelIds.value, label.id];
  tagsSearch.value.clear();
};

const createLabelIfNew = (name) => {
  if (labelNameTaken(name)) return;
  createLabel(name);
};

const closeDropdown = () => {
  if (drop.value?.opened) {
    drop.value.close();
  }
};

const handleOtherTaskLabelSelectOpened = (event) => {
  if (event.detail.token !== instanceToken) {
    closeDropdown();
  }
};

const onDropdownOpen = () => {
  window.dispatchEvent(new CustomEvent('task-label-select-open', {
    detail: { token: instanceToken },
  }));
  activeLabelIds.value = taskLabels.value.map(l => l.id);
};

// the first tag adds a row to the task card, moving the trigger out from under the panel
watch(() => taskLabels.value.length, async () => {
  await nextTick();
  drop.value.update();
});

onMounted(() => {
  window.addEventListener('task-label-select-open', handleOtherTaskLabelSelectOpened);
});

onBeforeUnmount(() => {
  window.removeEventListener('task-label-select-open', handleOtherTaskLabelSelectOpened);
});

defineExpose({
  open: () => {
    drop.value.activate();
  },
});
</script>
